import { useTranslation } from "react-i18next";
import TopBar from "@/pages/home/components/TopBar";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";
import StickyMobile from "@/pages/home/components/StickyMobile";
import Breadcrumb from "./components/Breadcrumb";
import Hero from "./components/Hero";
import IntroText from "./components/IntroText";
import DirectNaInbraak from "./components/DirectNaInbraak";
import Stappenplan from "./components/Stappenplan";
import WatWijHerstellen from "./components/WatWijHerstellen";
import Verzekering from "./components/Verzekering";
import SpoedInfo from "./components/SpoedInfo";
import Prijzen from "./components/Prijzen";
import BeveiligingPreventie from "./components/BeveiligingPreventie";
import WaaromWij from "./components/WaaromWij";
import FAQ from "./components/FAQ";
import InterneLinks from "./components/InterneLinks";
import CTABanner from "./components/CTABanner";
import SpoedForm from "./components/SpoedForm";

function SchemaMarkup() {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Inbraakschade Reparatie Eindhoven",
    provider: {
      "@type": "LocalBusiness",
      name: "Ramen Repair Eindhoven",
      url: "https://ramen-repair-eindhoven.nl/",
      telephone: "+31407987470",
      email: "info@ramen-repair-eindhoven.nl",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Marconilaan 3",
        postalCode: "5612 HM",
        addressLocality: "Eindhoven",
        addressRegion: "Noord-Brabant",
        addressCountry: "NL",
      },
      areaServed: {
        "@type": "City",
        name: "Eindhoven",
      },
    },
    serviceType: "Inbraakschade reparatie",
    description:
      "Inbraakschade aan uw raam, kozijn of deur ? Wij herstellen 24/7 met spoedservice, regelen verzekeringsdocumenten en plaatsen direct noodbeveiliging.",
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "EUR",
      lowPrice: "85",
      highPrice: "300",
    },
  };

  const emergencySchema = {
    "@context": "https://schema.org",
    "@type": "EmergencyService",
    name: "Ramen Repair Eindhoven — Inbraakschade spoedservice",
    areaServed: {
      "@type": "City",
      name: "Eindhoven",
    },
    availableChannel: {
      "@type": "ServiceChannel",
      servicePhone: {
        "@type": "ContactPoint",
        telephone: "+31407987470",
        contactType: "emergency",
        availableLanguage: "Dutch",
      },
    },
    openingHours: ["Mo-Su 00:00-23:59"],
    isAvailable: true,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://ramen-repair-eindhoven.nl/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Diensten",
        item: "https://ramen-repair-eindhoven.nl/onze-diensten/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Inbraakschade",
        item: "https://ramen-repair-eindhoven.nl/inbraakschade-reparatie/",
      },
    ],
  };

  const { t } = useTranslation("inbraakschade-reparatie");
  const faqItems = t("faq.items", { returnObjects: true }) as Array<{
    q: string;
    a: string;
  }>;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(emergencySchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
    </>
  );
}

export default function InbraakschadeReparatie() {
  const { t } = useTranslation("inbraakschade-reparatie");

  return (
    <main className="bg-white min-h-screen">
      <title>
        {t("seo.title", {
          defaultValue:
            "Inbraakschade reparatie Eindhoven | 24/7 spoedservice",
        })}
      </title>
      <meta
        name="description"
        content={t("seo.description", {
          defaultValue:
            "Inbraakschade aan uw raam, kozijn of deur ? Wij herstellen 24/7 met spoedservice, regelen verzekeringsdocumenten en plaatsen direct noodbeveiliging. Vanaf €95.",
        })}
      />
      <meta
        property="og:title"
        content={t("seo.title", {
          defaultValue:
            "Inbraakschade reparatie Eindhoven | 24/7 spoedservice",
        })}
      />
      <meta
        property="og:description"
        content={t("seo.description", {
          defaultValue:
            "Inbraakschade aan uw raam, kozijn of deur ? Wij herstellen 24/7 met spoedservice, regelen verzekeringsdocumenten en plaatsen direct noodbeveiliging. Vanaf €95.",
        })}
      />
      <meta property="og:locale" content="nl_NL" />
      <meta property="og:type" content="website" />
      <meta
        property="og:url"
        content="https://ramen-repair-eindhoven.nl/inbraakschade-reparatie/"
      />
      <meta
        property="og:image"
        content="https://readdy.ai/api/search-image?query=Professional%20craftsman%20hands%20working%20on%20modern%20window%20frame%20repair%20close-up%20tools%20yellow%20accents%20dark%20background%20editorial%20photography%20no%20faces%20sharp%20focus&width=1200&height=630&seq=ogimage2026&orientation=landscape"
      />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Ramen Repair Eindhoven - inbraakschade reparatie" />
      <link rel="canonical" href="https://ramen-repair-eindhoven.nl/inbraakschade-reparatie/" />
      <link rel="alternate" hreflang="nl-NL" href="https://ramen-repair-eindhoven.nl/inbraakschade-reparatie/" />
      <link rel="alternate" hreflang="x-default" href="https://ramen-repair-eindhoven.nl/inbraakschade-reparatie/" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={t("seo.title", { defaultValue: "Inbraakschade reparatie Eindhoven | 24/7 spoedservice" })} />
      <meta name="twitter:description" content={t("seo.description", { defaultValue: "Inbraakschade aan uw raam, kozijn of deur ? Wij herstellen 24/7 met spoedservice, regelen verzekeringsdocumenten en plaatsen direct noodbeveiliging. Vanaf €95." })} />
      <meta name="twitter:image" content="https://readdy.ai/api/search-image?query=Professional%20craftsman%20hands%20working%20on%20modern%20window%20frame%20repair%20close-up%20tools%20yellow%20accents%20dark%20background%20editorial%20photography%20no%20faces%20sharp%20focus&width=1200&height=630&seq=ogimage2026&orientation=landscape" />
      <SchemaMarkup />
      <TopBar />
      <Header />
      <Breadcrumb />
      <Hero />
      <IntroText />
      <DirectNaInbraak />
      <Stappenplan />
      <WatWijHerstellen />
      <Verzekering />
      <SpoedInfo />
      <Prijzen />
      <BeveiligingPreventie />
      <WaaromWij />
      <FAQ />
      <InterneLinks />
      <CTABanner />
      <SpoedForm />
      <Footer />
      <StickyMobile />
    </main>
  );
}